import { motion } from 'framer-motion';
import type { AppointmentRow } from '@/hooks/useAppointments';
import { theme } from '@/lib/theme';

interface Props {
  month: Date;
  appointments: AppointmentRow[];
  onSelectAppointment: (appt: AppointmentRow) => void;
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getStatusDot(status: string) {
  if (status === 'confirmed') return '#34d399';
  if (status === 'cancelled') return '#f87171';
  return '#c9a84c';
}

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export default function CalendarMonthView({ month, appointments, onSelectAppointment }: Props) {
  const today = new Date();
  const todayStr = toDateStr(today);

  // Build calendar grid
  const year = month.getFullYear();
  const monthIdx = month.getMonth();
  const firstDay = new Date(year, monthIdx, 1);
  const lastDay = new Date(year, monthIdx + 1, 0);
  const startOffset = firstDay.getDay(); // 0=Sun

  const cells: (Date | null)[] = [
    ...Array(startOffset).fill(null),
    ...Array.from({ length: lastDay.getDate() }, (_, i) => new Date(year, monthIdx, i + 1)),
  ];

  // Pad to complete last row
  while (cells.length % 7 !== 0) cells.push(null);

  const apptsByDate = appointments.reduce<Record<string, AppointmentRow[]>>((acc, appt) => {
    const key = appt.appointment_date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(appt);
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full">
      {/* Day name headers */}
      <div
        className="grid grid-cols-7 border-b"
        style={{ borderColor: theme.colors.border }}
      >
        {DAY_NAMES.map((name) => (
          <div
            key={name}
            className="py-3 text-center text-white/35 text-xs uppercase tracking-wider font-medium"
          >
            {name}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-7 h-full" style={{ gridAutoRows: 'minmax(100px, 1fr)' }}>
          {cells.map((day, idx) => {
            if (!day) {
              return (
                <div
                  key={`empty-${idx}`}
                  className="border-b border-r"
                  style={{ borderColor: theme.colors.border, backgroundColor: 'rgba(255,255,255,0.01)' }}
                />
              );
            }

            const ds = toDateStr(day);
            const isToday = ds === todayStr;
            const isCurrentMonth = day.getMonth() === monthIdx;
            const dayAppts = apptsByDate[ds] ?? [];
            const MAX_VISIBLE = 3;
            const overflow = dayAppts.length - MAX_VISIBLE;

            return (
              <motion.div
                key={ds}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: (idx % 7) * 0.02 }}
                className="border-b border-r p-2 flex flex-col gap-1 min-h-[100px]"
                style={{
                  borderColor: theme.colors.border,
                  backgroundColor: isToday ? 'rgba(201,168,76,0.04)' : 'transparent',
                }}
              >
                {/* Day number */}
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-semibold ${
                      isToday
                        ? 'text-[#0a0a0a]'
                        : isCurrentMonth
                        ? 'text-white/70'
                        : 'text-white/20'
                    }`}
                    style={{ backgroundColor: isToday ? theme.colors.primary : 'transparent' }}
                  >
                    {day.getDate()}
                  </span>
                  {dayAppts.length > 0 && (
                    <span
                      className="text-xs font-medium px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: theme.colors.primaryMuted, color: theme.colors.primary }}
                    >
                      {dayAppts.length}
                    </span>
                  )}
                </div>

                {/* Appointment pills */}
                {dayAppts.slice(0, MAX_VISIBLE).map((appt) => (
                  <button
                    key={appt.id}
                    onClick={() => onSelectAppointment(appt)}
                    className="w-full text-left rounded px-1.5 py-1 flex items-center gap-1.5 cursor-pointer transition-all hover:brightness-125 group"
                    style={{
                      backgroundColor: `${getStatusDot(appt.status)}18`,
                      border: `1px solid ${getStatusDot(appt.status)}30`,
                    }}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: getStatusDot(appt.status) }}
                    />
                    <span
                      className="text-xs truncate flex-1 font-medium"
                      style={{ color: getStatusDot(appt.status), fontSize: '10px' }}
                    >
                      {appt.start_time.slice(0, 5)} {appt.client_name}
                    </span>
                    <div className="w-3 h-3 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <i className="ri-arrow-right-s-line text-white/40" style={{ fontSize: '10px' }} />
                    </div>
                  </button>
                ))}

                {/* Overflow */}
                {overflow > 0 && (
                  <p className="text-white/30 text-xs pl-1">+{overflow} más</p>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
